// src/features/availability/services/availabilityService.ts
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';
import { ApiError } from '@/lib/errors';

import {
  assignmentBodySchema,
  availabilityRangeResponseSchema,
  availabilityRowSchema,
  blockAssignmentRowSchema,
  blockBodySchema,
  bookingRosterResponseSchema,
  searchAvailabilityResponseSchema,
  searchAvailabilitySchema,
  type AssignmentBody,
  type AvailabilityRangeResponse,
  type AvailabilityRow,
  type BlockAssignmentRow,
  type BlockBody,
  type BookingRosterResponse,
  type ListAvailabilityQuery,
  type SearchAvailabilityQuery,
  type SearchAvailabilityResponse,
} from '../types';

function parseOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first
      ? `${path ? `${path}: ` : ''}${first.message}`
      : 'Unexpected response shape';
    if (import.meta.env.DEV) {
      console.error(`[availabilityService] ${context} response failed schema parse:`, {
        issues: result.error.issues,
        raw,
      });
    }
    throw new ApiError({ status: 0, code: 'SHAPE_MISMATCH', message: `${context}: ${detail}` });
  }
  return result.data;
}

function validateInputOrThrow<T>(schema: z.ZodType<T>, raw: unknown, context: string): T {
  const result = schema.safeParse(raw);
  if (!result.success) {
    const first = result.error.issues[0];
    const path = first?.path.join('.') ?? '';
    const detail = first ? `${path ? `${path}: ` : ''}${first.message}` : 'Invalid input';
    throw new ApiError({
      status: 400,
      code: 'VALIDATION_ERROR',
      message: `${context}: ${detail}`,
    });
  }
  return result.data;
}

export const availabilityService = {
  async listRange(
    practitionerId: string,
    query: ListAvailabilityQuery,
  ): Promise<AvailabilityRangeResponse> {
    const { data } = await apiClient.get<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability`,
      { params: query },
    );
    return parseOrThrow(availabilityRangeResponseSchema, data.data, 'listRange');
  },

  async createBlock(practitionerId: string, body: BlockBody): Promise<AvailabilityRow> {
    const validated = validateInputOrThrow(blockBodySchema, body, 'createBlock');
    const { data } = await apiClient.post<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability/blocks`,
      validated,
    );
    return parseOrThrow(availabilityRowSchema, data.data, 'createBlock');
  },

  async updateBlock(
    practitionerId: string,
    blockId: string,
    body: BlockBody,
  ): Promise<AvailabilityRow> {
    const validated = validateInputOrThrow(blockBodySchema, body, 'updateBlock');
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability/blocks/${blockId}`,
      validated,
    );
    return parseOrThrow(availabilityRowSchema, data.data, 'updateBlock');
  },

  async deleteBlock(practitionerId: string, blockId: string): Promise<{ id: string }> {
    const { data } = await apiClient.delete<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability/blocks/${blockId}`,
    );
    return parseOrThrow(z.object({ id: z.string() }), data.data, 'deleteBlock');
  },

  async listAssignments(practitionerId: string, blockId: string): Promise<BlockAssignmentRow[]> {
    const { data } = await apiClient.get<{ data: unknown[] }>(
      `/practitioners/${practitionerId}/availability/blocks/${blockId}/assignments`,
    );
    return data.data.map((r) => parseOrThrow(blockAssignmentRowSchema, r, 'listAssignments'));
  },

  async createAssignment(
    practitionerId: string,
    blockId: string,
    body: AssignmentBody,
  ): Promise<BlockAssignmentRow> {
    const validated = validateInputOrThrow(assignmentBodySchema, body, 'createAssignment');
    const { data } = await apiClient.post<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability/blocks/${blockId}/assignments`,
      validated,
    );
    return parseOrThrow(blockAssignmentRowSchema, data.data, 'createAssignment');
  },

  async updateAssignment(
    practitionerId: string,
    blockId: string,
    assignmentId: string,
    body: AssignmentBody,
  ): Promise<BlockAssignmentRow> {
    const validated = validateInputOrThrow(assignmentBodySchema, body, 'updateAssignment');
    const { data } = await apiClient.patch<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability/blocks/${blockId}/assignments/${assignmentId}`,
      validated,
    );
    return parseOrThrow(blockAssignmentRowSchema, data.data, 'updateAssignment');
  },

  async deleteAssignment(
    practitionerId: string,
    blockId: string,
    assignmentId: string,
  ): Promise<{ id: string; blockId: string }> {
    const { data } = await apiClient.delete<{ data: unknown }>(
      `/practitioners/${practitionerId}/availability/blocks/${blockId}/assignments/${assignmentId}`,
    );
    return parseOrThrow(
      z.object({ id: z.string(), blockId: z.string() }),
      data.data,
      'deleteAssignment',
    );
  },

  async searchForClient(query: SearchAvailabilityQuery): Promise<SearchAvailabilityResponse> {
    const validated = validateInputOrThrow(searchAvailabilitySchema, query, 'searchForClient');
    const { data } = await apiClient.get<{ data: unknown }>(`/practitioners/availability/search`, {
      params: validated,
    });
    return parseOrThrow(searchAvailabilityResponseSchema, data.data, 'searchForClient');
  },

  async searchVolunteersForClient(
    query: SearchAvailabilityQuery,
  ): Promise<SearchAvailabilityResponse> {
    const validated = validateInputOrThrow(
      searchAvailabilitySchema,
      query,
      'searchVolunteersForClient',
    );
    const { data } = await apiClient.get<{ data: unknown }>(`/volunteers/availability/search`, {
      params: validated,
    });
    return parseOrThrow(searchAvailabilityResponseSchema, data.data, 'searchVolunteersForClient');
  },

  /** Every psychologist the student can book at their location, with free
   *  slots for the whole booking window, in one request. Client-only. */
  async bookingRosterForClient(): Promise<BookingRosterResponse> {
    const { data } = await apiClient.get<{ data: unknown }>(`/practitioners/availability/roster`);
    return parseOrThrow(bookingRosterResponseSchema, data.data, 'bookingRosterForClient');
  },

  /** Listener twin of `bookingRosterForClient`. */
  async volunteerBookingRosterForClient(): Promise<BookingRosterResponse> {
    const { data } = await apiClient.get<{ data: unknown }>(`/volunteers/availability/roster`);
    return parseOrThrow(bookingRosterResponseSchema, data.data, 'volunteerBookingRosterForClient');
  },
};
